import { useRef, useState } from "react";
import AvatarEditor, { type AvatarEditorRef } from "react-avatar-editor";
import {
  Box,
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Receives the cropped PNG blob produced by react-avatar-editor. */
  onCommit: (blob: Blob) => Promise<void> | void;
  busy?: boolean;
};

const CANVAS_SIZE = 250;
const ALLOWED = ["image/png", "image/jpeg", "image/webp"];

export default function AvatarEditorModal({ isOpen, onClose, onCommit, busy = false }: Props) {
  const editorRef = useRef<AvatarEditorRef | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setImage(null);
    setScale(1);
    setRotate(0);
    setError(null);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      setError("Pick a PNG, JPEG, or WebP image.");
      return;
    }
    setError(null);
    setImage(f);
  };

  const handleSave = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const canvas = editor.getImageScaledToCanvas();
    canvas.toBlob(
      async (blob: Blob | null) => {
        if (!blob) {
          setError("Couldn't generate image; try again.");
          return;
        }
        try {
          await onCommit(blob);
          reset();
        } catch (e) {
          setError((e as { message?: string })?.message ?? "Upload failed");
        }
      },
      "image/png",
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered closeOnOverlayClick={!busy}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit avatar</ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          {image ? (
            <VStack spacing={4} align="stretch">
              <Box display="flex" justifyContent="center">
                <AvatarEditor
                  ref={editorRef}
                  image={image}
                  width={CANVAS_SIZE}
                  height={CANVAS_SIZE}
                  border={20}
                  borderRadius={CANVAS_SIZE / 2}
                  color={[230, 230, 230, 0.7]}
                  scale={scale}
                  rotate={rotate}
                />
              </Box>
              <Box>
                <Text fontSize="sm" mb={1}>
                  Zoom
                </Text>
                <Slider
                  aria-label="zoom"
                  min={1}
                  max={3}
                  step={0.01}
                  value={scale}
                  onChange={setScale}
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>
              </Box>
              <HStack justify="space-between">
                <HStack>
                  <IconButton
                    aria-label="Rotate 90°"
                    icon={<RepeatIcon />}
                    size="sm"
                    onClick={() => setRotate((r) => (r + 90) % 360)}
                  />
                  <Button size="sm" variant="ghost" onClick={reset}>
                    Pick different image
                  </Button>
                </HStack>
              </HStack>
            </VStack>
          ) : (
            <VStack spacing={3} py={6}>
              <Text fontSize="sm" color="gray.600">
                Pick an image to crop into a circular avatar.
              </Text>
              <Button as="label" cursor="pointer" colorScheme="blue">
                Choose image
                <input
                  type="file"
                  hidden
                  accept={ALLOWED.join(",")}
                  onChange={handlePick}
                />
              </Button>
            </VStack>
          )}
          {error && (
            <Text color="red.500" fontSize="sm" mt={3}>
              {error}
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleClose} mr={3} isDisabled={busy}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isDisabled={!image}
            isLoading={busy}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
